import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { MsgService } from './msg.service';
import { ApiErrorResponse } from '../Interfaces/response.interface';
import { I18N_UNAVAILABLE_MESSAGE } from '../../../Consts/i18n-fallback.const';
import { Language } from '../Types/language.type';

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService {
  private msgService = inject(MsgService);
  private zone = inject(NgZone);
  private translateService = inject(TranslateService);

  public handleError(error: HttpErrorResponse): void {
    if (!environment.production) {
      console.error(error);
    }

    switch (error.status) {
      case 0:
        this.snackBarOpen(this.translateOrFallback('app.api.errors.network_unavailable'));
        return;

      case 401:
        return;

      default:
        this.snackBarOpen(this.extractErrorMessage(error));
    }
  }

  private snackBarOpen(message: string): void {
    this.zone.run(() => {
      this.msgService.showSnackBar(message, { type: 'error', duration: 5000 });
    });
  }

  private extractErrorMessage(error: HttpErrorResponse): string {
    const apiError = error.error as ApiErrorResponse | undefined;

    // The API envelope puts the i18n key in `message` and the HTTP status in `code`, so
    // `message` is the candidate that resolves for almost every error. A few rejections
    // (JWT, access decision) repeat the key in `code`, hence both are tried.
    const resolved =
      this.resolveTranslationKey(apiError?.message) ?? this.resolveTranslationKey(apiError?.code);

    if (resolved) {
      return resolved;
    }

    if (!environment.production) {
      console.warn('[i18n] no translation resolved for API error', {
        message: apiError?.message,
        code: apiError?.code,
      });
    }

    return this.translateOrFallback('shared.api.errors.default');
  }

  private translateOrFallback(key: string): string {
    const translated = this.translateService.instant(key);

    return typeof translated === 'string' && translated !== key
      ? translated
      : I18N_UNAVAILABLE_MESSAGE[this.resolveFallbackLanguage()];
  }

  /** The translation, but only when the candidate is a key the dictionary really has. */
  private resolveTranslationKey(candidate: unknown): string | null {
    if (typeof candidate !== 'string' || candidate.trim().length === 0) {
      return null;
    }

    const translated = this.translateService.instant(candidate);

    return typeof translated === 'string' && translated !== candidate ? translated : null;
  }

  private resolveFallbackLanguage(): Language {
    return this.translateService.currentLang === 'fr' ? 'fr' : 'en';
  }
}
